import React, { useEffect, useRef, useState } from "react";
import * as Styled from "./styles.js";
import Slider from "rc-slider";
import "rc-slider/assets/index.css";
import TextView from "../../../TextView/index.jsx";
import { Box, Tooltip, Typography } from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { ReactComponent as LockIcon } from "@assets/images/segmentEditor/Lock.svg";
import { ReactComponent as UnlockedIcon } from "@assets/images/segmentEditor/lockOpen.svg";

function SettingsSlider({
  sliderValue,
  onValueChange,
  title,
  tooltip,
  min = 0,
  max = 20,
  valueLabel = "px",
  containerStyle = {},
  applyButton = false,
  handleApplyChanges = () => {},
  allowedValues = [],
  step = 1,
  allowDecimal = false,
  fullSlider = false,
  locked = false,
  disabled = false,
  onLockToggle = () => {},
  // gordon-0925-P1-edit-wheel-segment-size-click-redirects-to-resize-pane:
  // opt-in (default false, no behaviour change for any existing caller) --
  // lets a caller that already renders its OWN value input elsewhere (the
  // shared Segment Size control's draft-buffer number box) reuse this
  // slider's drag/keyboard/rAF-throttle wiring without a second, redundant
  // input fighting the first one over the same value.
  hideValueInput = false,
  ...rest
}) {
  const [localValue, setLocalValue] = useState(sliderValue);

  // GORDON LAW 2026-09-15 03:25 PT: block-settings sliders must update the
  // rendered block LIVE while dragging, not only on release. `dragStartRef`
  // marks the start of the CURRENT drag gesture; `rafRef` throttles the live
  // onValueChange calls to at most one per animation frame so a fast drag
  // doesn't flood React with a render per pixel of mouse movement.
  const dragStartRef = useRef(null);
  const rafRef = useRef(null);
  // Safety buffer added on top of elapsed drag time when computing the
  // throttleRate hint passed to onValueChange. craft.js's History.throttleAdd
  // merges a setProp call into the CURRENT history entry when
  // `Date.now() - firstEntryTimestamp < throttleRate` — since that window is
  // measured from the first call in the entry (not a sliding window), we keep
  // growing the requested throttleRate just ahead of elapsed drag time so
  // every patch from one gesture (every live update + the final release
  // commit) merges into the same undo/history entry, no matter how long the
  // drag lasts.
  const DRAG_THROTTLE_SAFETY_BUFFER_MS = 1000;

  useEffect(() => {
    setLocalValue(sliderValue);
  }, [sliderValue]);

  useEffect(() => {
    return () => {
      if (rafRef.current != null) cancelAnimationFrame(rafRef.current);
    };
  }, []);

  const snapToAllowedValues = (value) => {
    if (!allowedValues.length) return value;
    let closest = allowedValues[0];
    let minDiff = Math.abs(value - closest);
    for (let i = 1; i < allowedValues.length; i++) {
      const diff = Math.abs(value - allowedValues[i]);
      if (diff < minDiff) {
        minDiff = diff;
        closest = allowedValues[i];
      }
    }

    return closest;
  };

  function snapToStep(value, step) {
    const snapped = Math.round(value / step) * step;

    const stepStr = String(step);
    const decimalPart = stepStr.split(".")[1];
    const decimals = decimalPart ? decimalPart.length : 0;

    // Fix floating artifact (e.g., 1.39999999999 => 1.40):
    return parseFloat(snapped.toFixed(decimals));
  }

  // Wired to rc-slider's onChangeComplete — fires ONCE on release.
  const handleSliderValueChange = (newValue) => {
    if (typeof newValue === "number" && newValue >= min && newValue <= max) {
      const snapped = snapToAllowedValues(newValue);
      if (rafRef.current != null) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }
      const throttleRate =
        dragStartRef.current != null
          ? Date.now() - dragStartRef.current + DRAG_THROTTLE_SAFETY_BUFFER_MS
          : undefined;
      onValueChange(snapped, throttleRate);
      setLocalValue(snapped);
      // Drag gesture is over — the next drag (or an unrelated interaction
      // like typing in the numeric box) should start a fresh window.
      dragStartRef.current = null;
    }
  };

  // Wired to rc-slider's onChange — fires continuously DURING drag. This is
  // the live-update path: it now ALSO calls onValueChange (rAF-throttled) so
  // the block actually renders the new value while the user is still
  // dragging, not only after they release.
  const handleSliderDragging = (newValue) => {
    if (typeof newValue === "number" && newValue >= min && newValue <= max) {
      const snapped = snapToAllowedValues(newValue);
      setLocalValue(snapped);

      if (dragStartRef.current == null) {
        dragStartRef.current = Date.now();
      }

      if (rafRef.current != null) cancelAnimationFrame(rafRef.current);
      rafRef.current = requestAnimationFrame(() => {
        rafRef.current = null;
        const throttleRate =
          Date.now() - dragStartRef.current + DRAG_THROTTLE_SAFETY_BUFFER_MS;
        onValueChange(snapped, throttleRate);
      });
    }
  };

  const handleValueChange = (e) => {
    const { value } = e.target;
    setLocalValue("");

    if (value.trim() === "") {
      onValueChange("");
      return;
    }

    if (allowDecimal) {
      if (value.endsWith(".")) {
        onValueChange(value);
        setLocalValue(value);
        return;
      }
      const parsedValue = parseFloat(value);
      if (!isNaN(parsedValue) && parsedValue >= min && parsedValue <= max) {
        const snapped = snapToStep(parsedValue, step);

        if (snapped >= min && snapped <= max) {
          onValueChange(snapped);
          setLocalValue(snapped);
        }
      }
      return;
    } else {
      const parsedValue = parseInt(value, 10);
      if (
        parsedValue >= min &&
        parsedValue <= max &&
        Number.isInteger(parsedValue)
      ) {
        onValueChange(parsedValue);
        setLocalValue(parsedValue);
      }
    }
  };

  const handleApplyClick = () => {
    handleApplyChanges(localValue);
  };

  const showHeader = !!title || !!tooltip || (fullSlider && !hideValueInput);

  return (
    <>
      <Styled.Container style={containerStyle}>
        {showHeader && (
          <Styled.SliderHeader>
            <Styled.Flexy>
              <TextView variant="subtitle">{title}</TextView>
              {!!tooltip && (
                <Tooltip title={tooltip} placement="top" arrow>
                  <InfoOutlinedIcon
                    sx={{
                      color: "#123051",
                      cursor: "pointer",
                    }}
                  />
                </Tooltip>
              )}
            </Styled.Flexy>

            {fullSlider && !hideValueInput && (
              <Styled.FlexyBaka>
                <Styled.StyledInput2
                  value={localValue}
                  disableUnderline
                  placeholder=""
                  onChange={handleValueChange}
                  disabled={disabled}
                />
                {valueLabel && <Styled.PxText2>{valueLabel}</Styled.PxText2>}
                {locked ? (
                  <LockIcon
                    style={{ height: "24px", width: "24px", cursor: "pointer" }}
                    onClick={onLockToggle}
                  />
                ) : (
                  <UnlockedIcon
                    style={{ height: "24px", width: "24px", cursor: "pointer" }}
                    onClick={onLockToggle}
                  />
                )}
              </Styled.FlexyBaka>
            )}
          </Styled.SliderHeader>
        )}

        <Styled.RowDiv>
          <Slider
            min={min}
            max={max}
            value={typeof localValue === "number" ? localValue : 0}
            onChange={handleSliderDragging}
            onChangeComplete={handleSliderValueChange}
            trackStyle={
              disabled
                ? { backgroundColor: "#e9e9e9", height: 8 }
                : { backgroundColor: "#9B5AF8", height: 8 }
            }
            handleStyle={
              disabled
                ? {
                    borderColor: "#e9e9e9",
                    backgroundColor: "#e9e9e9",
                    height: 24,
                    width: 24,
                    marginTop: -8,
                    marginLeft: "10px",
                  }
                : {
                    borderColor: "#9B5AF8",
                    backgroundColor: "#9B5AF8",
                    height: 24,
                    width: 24,
                    marginTop: -8,
                    marginLeft: "10px",
                  }
            }
            railStyle={
              disabled
                ? { backgroundColor: "#e9e9e9", height: 8 }
                : { backgroundColor: "#9B5AF860", height: 8 }
            }
            step={step}
            disabled={disabled}
            style={{ width: fullSlider && "95%" }}
            {...rest}
          />

          {!fullSlider && !hideValueInput && (
            <>
              <Styled.StyledInput
                value={localValue}
                disableUnderline
                placeholder=""
                onChange={handleValueChange}
              />
              {valueLabel && <Styled.PxText>{valueLabel}</Styled.PxText>}
            </>
          )}
        </Styled.RowDiv>
      </Styled.Container>

      {applyButton && (
        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            alignItems: "center",
            marginTop: "8px",
          }}
        >
          <Typography
            sx={{
              cursor: "pointer",
              color: "#9B5AF8",
              transition: "all 0.2s ease-in-out",
              fontSize: "16px",
              fontWeight: "500",
              fontFamily: "DM Sans",

              "&:hover": {
                scale: "1.05",
                transition: "all 0.2s ease-in-out",
              },
            }}
            onClick={handleApplyClick}
          >
            Apply
          </Typography>
        </Box>
      )}
    </>
  );
}

export default SettingsSlider;
